import { useMemo } from "react";

import { Activity } from "@packages/client";

import { ACTIVITY_CONTEXT } from "Constants/constants";
import useDecodedParams from "Hooks/useDecodedParams";
import useSelectorWithUrlParams from "Hooks/useSelectorWithUrlParams";
import getUrl from "Libs/getUrl";
import { environmentLoadingSelector } from "Reducers/environment";
import { gitProjectSelector } from "Reducers/project/project";
import { filterRecentActivities } from "src/organization/common/containers/ActivitySection/ActivitySection";
import { useAppSelector } from "Store/hooks";

import { getSelectors } from "./activity.selectors";

export const useEnvironmentActivities = ({
  allActivities,
  activitesToCheck,
  activityContext,
  environmentId
}) =>
  allActivities.some(
    activity =>
      !activitesToCheck.some(
        type => `${activityContext}.${type}` === activity.type
      ) && activity.environments.includes(environmentId)
  );

export const useShowFailedActivitySticker = () => {
  const { projectId, organizationId, environmentId, integrationId } =
    useDecodedParams<{
      projectId: string;
      organizationId: string;
      environmentId: string;
      integrationId: string;
    }>();

  const project = useSelectorWithUrlParams(gitProjectSelector);
  const envId = environmentId || project?.default_branch;

  const { completed } = getSelectors(ACTIVITY_CONTEXT.Environment, {
    projectId,
    organizationId,
    environmentId: envId,
    integrationId
  });
  const isEnvironmentLoading = useAppSelector(state =>
    environmentLoadingSelector(state, { environmentId: envId, projectId })
  );

  const completeActivities = useAppSelector(completed);
  const recentCompletedActivities = useMemo(
    () => filterRecentActivities(completeActivities),
    [completeActivities]
  );
  const isLastFailed =
    recentCompletedActivities[0]?.result === "failure" &&
    recentCompletedActivities[0]?.state !== "cancelled";

  const showFailedActivitySticker = !isEnvironmentLoading && isLastFailed;

  return {
    showFailedActivitySticker,
    lastFailedActivity: isLastFailed ? recentCompletedActivities[0] : undefined
  };
};

export const getLogUrl = ({
  activity,
  organizationId,
  projectId,
  environmentId,
  integrationId
}: {
  activity?: Activity;
  organizationId: string;
  projectId: string;
  environmentId: string;
  integrationId?: string;
}): string | undefined => {
  if (!activity) return undefined;
  const props = {
    organizationId,
    projectId,
    environmentId: environmentId ?? "",
    integrationId: integrationId ?? "",
    activityId: activity.id
  };

  if (environmentId) {
    return getUrl({
      key: "organization.project.environment.log",
      props
    });
  } else if (integrationId) {
    return getUrl({
      key: "organization.project.settings.integrations.integration.log",
      props
    });
  } else {
    return getUrl({
      key: "organization.project.log",
      props
    });
  }
};
